import React, { useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider
} from "@mui/material";
import { 
  Add, 
  Edit, 
  Delete, 
  Save, 
  Cancel, 
  Close,
  Visibility,
  ArrowBack,
  CheckCircle,
  Cancel as CancelIcon,
  NavigateNext,
  Menu as MenuIcon,
  Person,
  SupervisorAccount
} from "@mui/icons-material";

const Team = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Tab state for mobile
  const [mobileTab, setMobileTab] = useState(0);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  
  // Table data states
  const [reportingManagerData, setReportingManagerData] = useState([]);
  const [directReportsData, setDirectReportsData] = useState([]);

  // Editing states
  const [editingReportingManager, setEditingReportingManager] = useState(null);
  const [editingDirectReports, setEditingDirectReports] = useState(null);

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingRow, setViewingRow] = useState(null);
  const [viewingSection, setViewingSection] = useState("");

  // New row states
  const [newReportingManager, setNewReportingManager] = useState({
    name: "", 
    type: "", 
    department: "", 
    designation: "", 
    from: "", 
    to: ""
  });
  const [newDirectReport, setNewDirectReport] = useState({
    name: "", 
    subDepartment: "", 
    designation: "", 
    from: "", 
    to: ""
  });

  // Adding states
  const [addingReportingManager, setAddingReportingManager] = useState(false);
  const [addingDirectReports, setAddingDirectReports] = useState(false);

  // Demo data
  React.useEffect(() => {
    setReportingManagerData([
      {
        id: 1,
        name: "Michael Johnson",
        type: "Permanent",
        department: "IT",
        designation: "Senior Manager",
        from: "2022-01-15",
        to: "Present",
        email: "michael.j@company.com",
        phone: "+1 234 567 8901"
      },
      {
        id: 2,
        name: "Sarah Williams",
        type: "Permanent",
        department: "HR",
        designation: "HR Director",
        from: "2021-06-01",
        to: "Present",
        email: "sarah.w@company.com",
        phone: "+1 234 567 8902"
      },
    ]);
    
    setDirectReportsData([
      {
        id: 1,
        name: "Alex Turner",
        subDepartment: "Frontend",
        designation: "Senior Developer",
        from: "2023-03-10",
        to: "Present",
        email: "alex.t@company.com",
        phone: "+1 234 567 8903"
      },
      {
        id: 2,
        name: "Emma Davis",
        subDepartment: "Backend",
        designation: "Team Lead",
        from: "2022-11-05",
        to: "Present",
        email: "emma.d@company.com",
        phone: "+1 234 567 8904"
      },
      {
        id: 3,
        name: "James Wilson",
        subDepartment: "QA",
        designation: "QA Engineer",
        from: "2023-08-20",
        to: "Present",
        email: "james.w@company.com",
        phone: "+1 234 567 8905"
      },
    ]);
  }, []);

  // Common handlers
  const handleAdd = (table, newRow, setData, setNewRow, setAdding) => {
    if (Object.values(newRow).some(val => !val)) {
      alert("Please fill all fields!");
      return;
    }
    setData([{ ...newRow, id: Date.now() }, ...table]);
    setNewRow(Object.fromEntries(Object.keys(newRow).map(key => [key, ""])));
    setAdding(false);
  };

  const handleEdit = (id, setEditing) => setEditing(id);
  const handleCancel = (setEditing, setAdding) => {
    if (setEditing) setEditing(null);
    if (setAdding) setAdding(false);
  };
  
  const handleDelete = (id, table, setData) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      setData(table.filter(row => row.id !== id));
    }
  };

  const handleChange = (id, field, value, table, setData) => {
    setData(table.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const handleNewChange = (field, value, newRow, setNewRow) => {
    setNewRow({ ...newRow, [field]: value });
  };

  const handleView = (row, section) => {
    setViewingRow(row);
    setViewingSection(section);
    setViewDialogOpen(true);
  };

  const startAdding = (setAdding) => {
    setAdding(true);
  };

  // Table configurations
  const tables = [
    {
      id: "reporting",
      title: "REPORTING MANAGER",
      data: reportingManagerData,
      setData: setReportingManagerData,
      editing: editingReportingManager,
      setEditing: setEditingReportingManager,
      newRow: newReportingManager,
      setNewRow: setNewReportingManager,
      adding: addingReportingManager,
      setAdding: setAddingReportingManager,
      columns: [
        { label: "Name", field: "name" },
        { label: "Type", field: "type" },
        { label: "Department", field: "department" },
        { label: "Designation", field: "designation" },
        { label: "From", field: "from" },
        { label: "To", field: "to" }
      ],
      mobileTitle: "Managers",
      icon: <SupervisorAccount />,
      color: "#7564e6"
    },
    {
      id: "direct",
      title: "DIRECT REPORTS",
      data: directReportsData,
      setData: setDirectReportsData,
      editing: editingDirectReports,
      setEditing: setEditingDirectReports,
      newRow: newDirectReport,
      setNewRow: setNewDirectReport,
      adding: addingDirectReports,
      setAdding: setAddingDirectReports,
      columns: [
        { label: "Name", field: "name" },
        { label: "Sub Department", field: "subDepartment" },
        { label: "Designation", field: "designation" },
        { label: "From", field: "from" },
        { label: "To", field: "to" }
      ],
      mobileTitle: "Reports",
      icon: <Person />,
      color: "#7564e6"
    }
  ];

  // Mobile View with Enhanced Design
  const renderMobileView = () => {
    const currentTable = tables[mobileTab];
    
    return (
      <Box sx={{ 
        height: "100vh", 
        overflow: "hidden",
        backgroundColor: "#f8f9fa",
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Enhanced Header */}
        <Box sx={{ 
          background: `linear-gradient(135deg, ${currentTable.color} 0%, ${currentTable.color}99 100%)`,
          color: "white",
          py: 2.5,
          px: 2,
          position: 'relative',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/*  */}
            <Typography variant="h6" fontWeight="bold" sx={{ textAlign: 'center', flex: 1 }}>
              Team Management
            </Typography>
            <Box sx={{ width: 40 }} /> {/* Spacer for alignment */}
          </Box>
          
          {/* Quick Stats */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1.5 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                {reportingManagerData.length}
              </Typography>
              <Typography variant="caption">
                Managers
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                {directReportsData.length}
              </Typography>
              <Typography variant="caption">
                Reports
              </Typography>
            </Box>
          </Box>
        </Box>

        

        {/* Enhanced Tabs */}
        <Box sx={{ 
          backgroundColor: "white",
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <Tabs
            value={mobileTab}
            onChange={(e, newValue) => setMobileTab(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                minHeight: 56,
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: '#666',
                '&.Mui-selected': {
                  color: currentTable.color,
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: currentTable.color,
                height: 3
              }
            }}
          >
            {tables.map((table, index) => (
              <Tab 
                key={table.id} 
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {table.icon}
                    <Typography variant="caption" sx={{ mt: 0.5 }}>
                      {table.mobileTitle}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/* Main Content */}
        <Box sx={{ 
          flex: 1,
          overflowY: "auto",
          p: 2
        }}>
          <Box sx={{ mb: 3 }}>
            {/* Section Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3 
            }}>
              <Box>
                <Typography variant="h6" sx={{ 
                  color: currentTable.color,
                  fontWeight: 'bold',
                  mb: 0.5
                }}>
                  {currentTable.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total: {currentTable.data.length} records
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => startAdding(currentTable.setAdding)}
                sx={{
                  backgroundColor: currentTable.color,
                  borderRadius: "20px",
                  px: 2,
                  py: 1,
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  "&:hover": {
                    backgroundColor: currentTable.color,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                Add New
              </Button>
            </Box>

            {/* Add Form */}
            {currentTable.adding && (
              <Card sx={{ 
                mb: 3, 
                backgroundColor: "#f0f7ff", 
                border: `2px solid ${currentTable.color}`,
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                <CardContent>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 2 
                  }}>
                    <Typography variant="subtitle1" sx={{ 
                      color: currentTable.color, 
                      fontWeight: 'bold' 
                    }}>
                      <Add sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Add New Record
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleCancel(null, currentTable.setAdding)}
                      sx={{ color: '#666' }}
                    >
                      <Close />
                    </IconButton>
                  </Box>
                  
                  <Grid container spacing={2}>
                    {currentTable.columns.map((column) => (
                      <Grid item xs={12} sm={6} key={`new-mobile-${column.field}`}>
                        <TextField
                          fullWidth
                          size="small"
                          label={column.label}
                          value={currentTable.newRow[column.field]}
                          onChange={(e) =>
                            handleNewChange(
                              column.field,
                              e.target.value,
                              currentTable.newRow,
                              currentTable.setNewRow
                            )
                          }
                          sx={{ 
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '10px',
                              backgroundColor: 'white'
                            }
                          }}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        gap: 2, 
                        mt: 1 
                      }}>
                        <Button
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancel(null, currentTable.setAdding)}
                          sx={{
                            borderRadius: '20px',
                            borderColor: '#ddd',
                            color: '#666'
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<Save />}
                          onClick={() =>
                            handleAdd(
                              currentTable.data,
                              currentTable.newRow,
                              currentTable.setData,
                              currentTable.setNewRow,
                              currentTable.setAdding
                            )
                          }
                          sx={{
                            backgroundColor: currentTable.color,
                            borderRadius: '20px',
                            "&:hover": {
                              backgroundColor: currentTable.color
                            }
                          }}
                        >
                          Save Record
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Data Cards */}
            {currentTable.data.map((row) => (
              <Card key={row.id} sx={{ 
                mb: 2, 
                border: "1px solid #e0e0e0",
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                }
              }}>
                <CardContent>
                  {currentTable.editing === row.id ? (
                    // Edit Mode
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        mb: 2 
                      }}>
                        <Typography variant="subtitle1" sx={{ 
                          color: '#ff9800', 
                          fontWeight: 'bold' 
                        }}>
                          <Edit sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Editing Record
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => handleCancel(currentTable.setEditing)}
                          sx={{ color: '#666' }}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                      
                      <Grid container spacing={1}>
                        {currentTable.columns.map((column) => (
                          <Grid item xs={12} key={`edit-mobile-${column.field}`}>
                            <TextField
                              fullWidth
                              size="small"
                              label={column.label}
                              value={row[column.field]}
                              onChange={(e) =>
                                handleChange(
                                  row.id,
                                  column.field,
                                  e.target.value,
                                  currentTable.data,
                                  currentTable.setData
                                )
                              }
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'flex-end', 
                            gap: 2, 
                            mt: 2 
                          }}>
                            <Button
                              variant="outlined"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancel(currentTable.setEditing)}
                              sx={{
                                borderRadius: '20px',
                                borderColor: '#ddd',
                                color: '#666'
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<Save />}
                              onClick={() => currentTable.setEditing(null)}
                              sx={{
                                backgroundColor: currentTable.color,
                                borderRadius: '20px',
                                "&:hover": {
                                  backgroundColor: currentTable.color
                                }
                              }}
                            >
                              Save Changes
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    // View Mode
                    <Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start', 
                        mb: 2 
                      }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                            color: currentTable.color,
                            mb: 0.5
                          }}>
                            {row[currentTable.columns[0].field]}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={row[currentTable.columns[1].field]}
                              size="small"
                              sx={{
                                backgroundColor: currentTable.color + '20',
                                color: currentTable.color,
                                fontWeight: 'medium'
                              }}
                            />
                            {row.email && (
                              <Typography variant="caption" color="text.secondary">
                                {row.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box>
                          <IconButton 
                            size="small" 
                            onClick={() => handleView(row, currentTable.title)}
                            sx={{ 
                              color: "#7564e6",
                              backgroundColor: '#7564e610',
                              mr: 0.5
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(row.id, currentTable.setEditing)}
                            sx={{ 
                              color: "#ff9800",
                              backgroundColor: '#ff980010',
                              mr: 0.5
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(row.id, currentTable.data, currentTable.setData)}
                            sx={{ 
                              color: "#f44336",
                              backgroundColor: '#f4433610'
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Grid container spacing={1}>
                        {currentTable.columns.slice(2).map((column, idx) => (
                          <Grid item xs={6} key={`view-mobile-${column.field}`}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {column.label}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {row[column.field] || "-"}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                      
                      {row.phone && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #eee' }}>
                          <Typography variant="caption" color="text.secondary">
                            Contact:
                          </Typography>
                          <Typography variant="body2">
                            {row.phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}

            {currentTable.data.length === 0 && !currentTable.adding && (
              <Box sx={{ 
                textAlign: 'center', 
                py: 6,
                backgroundColor: 'white',
                borderRadius: '16px',
                border: '2px dashed #ddd'
              }}>
                <Box sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  backgroundColor: currentTable.color + '20',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}>
                  {React.cloneElement(currentTable.icon, { 
                    sx: { 
                      color: currentTable.color,
                      fontSize: 30
                    } 
                  })}
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No {currentTable.mobileTitle.toLowerCase()} found
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => startAdding(currentTable.setAdding)}
                  sx={{
                    backgroundColor: currentTable.color,
                    borderRadius: '20px',
                    px: 3
                  }}
                >
                  Add First {currentTable.mobileTitle.slice(0, -1)}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Enhanced Bottom Navigation */}
        
      </Box>
    );
  };

  // Desktop/Tablet View
  const renderDesktopView = () => {
    return (
      <Box
        sx={{
          height: "100vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: 4 },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
          p: { xs: 2, sm: 3 },
          backgroundColor: "white",
        }}
      >
        {tables.map((table, tableIndex) => (
          <Box key={tableIndex} sx={{ mb: 6 }}>
            {/* Enhanced Header with Stats */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2,
              backgroundColor: table.color + '10',
              p: 2,
              borderRadius: '12px',
              borderLeft: `4px solid ${table.color}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  backgroundColor: table.color,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {React.cloneElement(table.icon, { sx: { color: 'white', fontSize: 20 } })}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: table.color,
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}
                  >
                    {table.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {table.data.length} records • Last updated: Today
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => {
                    if (table.data.length > 0) {
                      handleView(table.data[0], table.title);
                    }
                  }}
                  disabled={table.data.length === 0}
                  sx={{
                    borderRadius: "20px",
                    borderColor: table.color,
                    color: table.color,
                    '&:hover': {
                      borderColor: table.color,
                      backgroundColor: table.color + '10'
                    }
                  }}
                >
                  Quick View
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => startAdding(table.setAdding)}
                  sx={{
                    backgroundColor: table.color,
                    borderRadius: "20px",
                    px: 3,
                    fontWeight: 'bold',
                    boxShadow: '0 2px 8px ' + table.color + '40',
                    "&:hover": {
                      backgroundColor: table.color,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px ' + table.color + '60'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  Add New
                </Button>
              </Box>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                border: "1px solid #ddd",
                mb: 2,
                overflowX: "auto",
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
              }}
            >
              <Table sx={{ minWidth: isTablet ? 800 : "auto" }}>
                <TableHead>
                  <TableRow sx={{ 
                    backgroundColor: table.color + '15',
                    '& th': {
                      borderColor: table.color + '30'
                    }
                  }}>
                    {table.columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          border: '1px solid #ddd',
                          py: 2,
                          fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          whiteSpace: "nowrap",
                          color: table.color
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        border: '1px solid #ddd',
                        py: 2,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        color: table.color
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Add Row */}
                  {table.adding && (
                    <TableRow sx={{ backgroundColor: table.color + '08' }}>
                      {table.columns.map((column) => (
                        <TableCell
                          key={`new-${column.field}`}
                          sx={{ border: '1px solid #ddd', py: 1.5 }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={table.newRow[column.field]}
                            onChange={(e) =>
                              handleNewChange(
                                column.field,
                                e.target.value,
                                table.newRow,
                                table.setNewRow
                              )
                            }
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: 'white'
                              }
                            }}
                          />
                        </TableCell>
                      ))}
                      <TableCell
                        align="center"
                        sx={{ border: '1px solid #ddd', py: 1.5 }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Save />}
                            onClick={() =>
                              handleAdd(
                                table.data,
                                table.newRow,
                                table.setData,
                                table.setNewRow,
                                table.setAdding
                              )
                            }
                            sx={{
                              backgroundColor: "#29BF2E",
                              borderRadius: '20px',
                              px: 2,
                              "&:hover": { backgroundColor: "#1e8e25" }
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => handleCancel(null, table.setAdding)}
                            sx={{
                              borderRadius: '20px',
                              px: 2,
                              borderColor: "#f44336",
                              color: "#f44336",
                              "&:hover": { 
                                borderColor: "#d32f2f",
                                backgroundColor: "#f4433610"
                              }
                            }}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Data Rows */}
                  {table.data.map((row) => (
                    <TableRow 
                      key={row.id} 
                      hover
                      sx={{ 
                        '&:hover': {
                          backgroundColor: table.color + '05'
                        }
                      }}
                    >
                      {table.editing === row.id ? (
                        <>
                          {table.columns.map((column) => (
                            <TableCell
                              key={`edit-${column.field}`}
                              sx={{ border: '1px solid #ddd', py: 1.5 }}
                            >
                              <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                value={row[column.field]}
                                onChange={(e) =>
                                  handleChange(
                                    row.id,
                                    column.field,
                                    e.target.value,
                                    table.data,
                                    table.setData
                                  )
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: 'white'
                                  }
                                }}
                              />
                            </TableCell>
                          ))}
                          <TableCell
                            align="center"
                            sx={{ border: '1px solid #ddd', py: 1.5 }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<Save />}
                                onClick={() => table.setEditing(null)}
                                sx={{
                                  backgroundColor: "#29BF2E",
                                  borderRadius: '20px',
                                  px: 2,
                                  "&:hover": { backgroundColor: "#1e8e25" }
                                }}
                              >
                                Save
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Cancel />}
                                onClick={() => handleCancel(table.setEditing)}
                                sx={{
                                  borderRadius: '20px',
                                  px: 2,
                                  borderColor: "#f44336",
                                  color: "#f44336",
                                  "&:hover": { 
                                    borderColor: "#d32f2f",
                                    backgroundColor: "#f4433610"
                                  }
                                }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          {table.columns.map((column) => (
                            <TableCell
                              key={`view-${column.field}`}
                              align="center"
                              sx={{ 
                                border: '1px solid #ddd', 
                                py: 1.5,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                maxWidth: "200px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}
                              title={row[column.field]}
                            >
                              {column.field === 'type' || column.field === 'department' || column.field === 'subDepartment' ? (
                                <Chip
                                  label={row[column.field]}
                                  size="small"
                                  sx={{
                                    backgroundColor: table.color + '20',
                                    color: table.color,
                                    fontWeight: 'medium'
                                  }}
                                />
                              ) : (
                                row[column.field] || "-"
                              )}
                            </TableCell>
                          ))}
                          <TableCell
                            align="center"
                            sx={{ border: '1px solid #ddd', py: 1.5 }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => handleView(row, table.title)}
                                sx={{
                                  borderRadius: '20px',
                                  px: 1.5,
                                  borderColor: "#7564e6",
                                  color: "#7564e6",
                                  "&:hover": { 
                                    borderColor: "#0d47a1",
                                    backgroundColor: "#7564e610"
                                  }
                                }}
                              >
                                View
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Edit />}
                                onClick={() => handleEdit(row.id, table.setEditing)}
                                sx={{
                                  borderRadius: '20px',
                                  px: 1.5,
                                  borderColor: "#ff9800",
                                  color: "#ff9800",
                                  "&:hover": { 
                                    borderColor: "#f57c00",
                                    backgroundColor: "#ff980010"
                                  }
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Delete />}
                                onClick={() =>
                                  handleDelete(row.id, table.data, table.setData)
                                }
                                sx={{
                                  borderRadius: '20px',
                                  px: 1.5,
                                  borderColor: "#f44336",
                                  color: "#f44336",
                                  "&:hover": { 
                                    borderColor: "#d32f2f",
                                    backgroundColor: "#f4433610"
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}

                  {table.data.length === 0 && !table.adding && (
                    <TableRow>
                      <TableCell 
                        colSpan={table.columns.length + 1}
                        align="center"
                        sx={{ py: 6, color: "text.secondary" }}
                      >
                        <Box sx={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: '50%', 
                          backgroundColor: table.color + '15',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2
                        }}>
                          {React.cloneElement(table.icon, { 
                            sx: { 
                              color: table.color,
                              fontSize: 40
                            } 
                          })}
                        </Box>
                        <Typography variant="h6" sx={{ mb: 1, color: table.color }}>
                          No {table.mobileTitle.toLowerCase()} found
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Get started by adding your first {table.mobileTitle.slice(0, -1).toLowerCase()}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={() => startAdding(table.setAdding)}
                          sx={{
                            backgroundColor: table.color,
                            borderRadius: '20px',
                            px: 3,
                            py: 1
                          }}
                        >
                          Add First {table.mobileTitle.slice(0, -1)}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}

        {/* Enhanced Action Buttons */}
        
      </Box>
    );
  };

  // View Details Dialog
  const renderViewDialog = () => {
    if (!viewingRow) return null;
    
    const currentTable = tables.find(t => t.title === viewingSection) || tables[0];
    
    return (
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${currentTable.color} 0%, ${currentTable.color}99 100%)`,
          color: "white",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {currentTable.icon}
            <Typography variant="h6" fontWeight="bold">
              {viewingSection} Details
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setViewDialogOpen(false)} 
            sx={{ 
              color: "white",
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            backgroundColor: currentTable.color + '08',
            p: 2,
            borderRadius: '12px',
            mb: 3
          }}>
            <Typography variant="h6" sx={{ color: currentTable.color, mb: 0.5 }}>
              {viewingRow[currentTable.columns[0].field]}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewingRow.designation || viewingRow[currentTable.columns[2]?.field]}
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            {currentTable.columns.map((column) => (
              <Grid item xs={12} key={`dialog-${column.field}`}>
                <Typography variant="subtitle2" color="text.secondary">
                  {column.label}
                </Typography>
                <Typography variant="body1" sx={{ 
                  mt: 0.5,
                  p: 1.5,
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #eee'
                }}>
                  {viewingRow[column.field] || "Not specified"}
                </Typography>
              </Grid>
            ))}
            {viewingRow.email && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ 
                  mt: 0.5,
                  p: 1.5,
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #eee'
                }}>
                  {viewingRow.email}
                </Typography>
              </Grid>
            )}
            {viewingRow.phone && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ 
                  mt: 0.5,
                  p: 1.5,
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #eee'
                }}>
                  {viewingRow.phone}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setViewDialogOpen(false)}
            variant="contained"
            sx={{ 
              borderRadius: "20px",
              backgroundColor: currentTable.color,
              px: 4,
              "&:hover": { backgroundColor: currentTable.color }
            }}
          >
            Close Details
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <>
      {isMobile ? renderMobileView() : renderDesktopView()}
      {renderViewDialog()}
    </>
  );
};

export default Team;